/**
 * First-touch lead attribution kept in sessionStorage (no cookies, no consent needed:
 * it is first-party data only sent together with a form the visitor submits).
 */

const KEY = "tt_attribution_v1";

export type Attribution = {
  landing_page: string | null;
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
};

type StorageLike = Pick<Storage, "getItem" | "setItem">;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"] as const;

const clip = (v: string | null | undefined, max = 500) => (v ? v.slice(0, max) : null);

/** Builds the attribution for a first visit. Pure, for tests. */
export const buildAttribution = (href: string, referrer: string): Attribution => {
  const url = new URL(href);
  let ref: string | null = null;
  if (referrer) {
    try {
      ref = new URL(referrer).host === url.host ? null : referrer;
    } catch {
      ref = null;
    }
  }
  const out: Attribution = {
    landing_page: clip(url.pathname + url.search),
    referrer: clip(ref),
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    gclid: null,
  };
  for (const k of UTM_KEYS) out[k] = clip(url.searchParams.get(k), 200);
  return out;
};

/** Stores the attribution only once per session (first touch wins). */
export const captureAttribution = (
  href: string,
  referrer: string,
  storage: StorageLike,
): Attribution => {
  try {
    const existing = storage.getItem(KEY);
    if (existing) return JSON.parse(existing) as Attribution;
  } catch {
    /* fall through */
  }
  const a = buildAttribution(href, referrer);
  try {
    storage.setItem(KEY, JSON.stringify(a));
  } catch {
    /* storage unavailable */
  }
  return a;
};

/** Client-only: call from an effect. */
export const captureAttributionInBrowser = () => {
  if (typeof window === "undefined") return;
  captureAttribution(window.location.href, document.referrer, window.sessionStorage);
};

export type LeadContext = Attribution & { submit_page: string | null; idioma: string };

/** Attribution + submit page + language, ready to merge into a lead row. */
export const getLeadContext = (lang: string): LeadContext => {
  if (typeof window === "undefined") {
    return { ...buildAttribution("https://techtail.es/", ""), submit_page: null, idioma: lang };
  }
  const a = captureAttribution(window.location.href, document.referrer, window.sessionStorage);
  return {
    ...a,
    submit_page: clip(window.location.pathname + window.location.search),
    idioma: (lang || "es").substring(0, 2),
  };
};
